import { utils } from '@hh/global.js'

/**
 * This project is using Swiper as an element (web component).
 * Therefore, there are 2 web components (custom elements) available for usage:
 *    <swiper-container> - main Swiper element
 *    <swiper-slide> - Swiper slide element
 *
 * This hh-swiper component allows us to pass in more complex parameters objects as `HTMLElement` properties (https://swiperjs.com/element#parameters-as-props)
 * It is important to add the `init='false'` attribute on the <swiper-container> to prevent Swiper from initialization until we pass all required parameters.
 *
 * Example usage:
 *  assign id = id
 *  <hh-swiper
 *    data-slides-per-view='1.5,2.5,3'
 *    data-custom-previous='.swiper-button-prev--{{ id }}'
      data-custom-next='.swiper-button-prev--{{ id }}'
 *  >
 *    <swiper-container init="false">
 *      <swiper-slide>Slide 1</swiper-slide>
 *      <swiper-slide>Slide 2</swiper-slide>
 *      <swiper-slide>Slide 3</swiper-slide>
 *    </swiper-container>
 *    <div class='swiper-button-prev swiper-button-prev--{{ id }}></div>
 *    <div class='swiper-button-next swiper-button-next--{{ id }}></div>
 *  </hh-swiper>
 *
 *
 * use data-autoplay="true" to enable autoplay, and add a data-speed
 * use data-mobile-nav="true" to enable navigation on mobile (ie, the product card images and announcement bar don't show arrows on mobile)
 * data-slides-per-view should be a comma separated string of three values for mobile, tablet, and desktop. ex: "1,2,3"
 * data-space-between is currently configured to be 20 on mobile, 40 on desktop. If needed, this could be made more flexible with more data attributes
 * data-custom-next and data-custom-previous can be used to specify custom classes for the next and previous buttons. See the `product-feed` section for how it's done
 * data-effect can be fade, cube, flip.. all options from swiper.js. Default is slide
 */

const setSlidesA11y = (swiper, shouldntAutoplay) => {
  if (shouldntAutoplay) {
    setTimeout(() => {
      swiper.querySelector('.swiper-notification')?.remove()
      swiper.querySelector('.swiper-wrapper').removeAttribute('aria-live')
    }, 0)
  }

  // if the slider is disabled, we shouldn't be worried about making slides inert
  if (!swiper.classList.contains('swiper-scrollbar-disabled')) {
    swiper.querySelectorAll('.swiper-slide').forEach((slide) => {
      if (slide.classList.contains('swiper-slide-active')) {
        slide.removeAttribute('inert')
      } else {
        slide.setAttribute('inert', '')
      }
    })
  }
}

class HHSwiper extends HTMLElement {
  connectedCallback() {
    const swiper = this.querySelector('swiper-container')

    const swiperParams = {
      lazy: true,
      watchSlidesProgress: true,
      lazyPreloadPrevNext: true,
      a11y: true,
      direction: this.dataset.verticalScroll ? 'vertical' : 'horizontal',
      loop: !Boolean(this.dataset.cancelLoop),
      effect: this.dataset.effect ? this.dataset.effect : 'slide',
      fadeEffect: {
        crossFade: true,
      },
      navigation: {
        nextEl: this.dataset.customNext
          ? document.querySelector(this.dataset.customNext)
          : this.querySelector(`.swiper-button-next--${this.dataset.id}`),
        prevEl: this.dataset.customPrevious
          ? document.querySelector(this.dataset.customPrevious)
          : this.querySelector(`.swiper-button-prev--${this.dataset.id}`),
        enabled: Boolean(this.dataset.mobileNav),
      },
      pagination: { el: `.swiper-pagination--${this.dataset.id}` },
      slidesPerView: this.dataset.slidesPerView
        ? parseFloat(this.dataset.slidesPerView.split(',')[0])
        : 1,
      scrollbar: {
        el: `.swiper-scrollbar--${this.dataset.id}`,
      },
      spaceBetween: this.dataset.spaceBetween ? 20 : 0,
      breakpoints: {
        750: {
          slidesPerView: this.dataset.slidesPerView
            ? parseFloat(this.dataset.slidesPerView.split(',')[1])
            : 1,
          spaceBetween: this.dataset.spaceBetween ? 20 : 0,
          scrollbar: { enabled: false },
        },
        990: {
          slidesPerView: this.dataset.slidesPerView
            ? parseFloat(this.dataset.slidesPerView.split(',')[2])
            : 1,
          spaceBetween: this.dataset.spaceBetween
            ? this.dataset.spaceBetweenPx
              ? this.dataset.spaceBetweenPx
              : 0
            : 0,
          scrollbar: { enabled: false },
        },
      },
      autoplay: Boolean(this.dataset.autoplay)
        ? { delay: parseInt(this.dataset.speed * 1000) }
        : false,
      autoHeight: Boolean(this.dataset.autoheight),
      calculateHeight: Boolean(this.dataset.calculateheight),
      nested: Boolean(this.dataset.nested),
      mousewheel: {
        forceToAxis: true,
      },
      watchSlideProgress: true,
      on: {
        init() {
          setSlidesA11y(this.el, Boolean(this.autoplay))
        },
        slideChangeTransitionEnd() {
          setSlidesA11y(this.el, Boolean(this.autoplay))
        },
      },
    }

    Object.assign(swiper, swiperParams)

    swiper?.initialize()

    if (this.dataset.mobileOnly) {
      const swiperResponsiveToggle = () => {
        const isDesktop = window.innerWidth > 800

        if (isDesktop && this.swiper.initialized) {
          this.swiper.destroy(false, true)
        } else if (!isDesktop && this.swiper.destroyed) {
          this.swiper.init()
        }
      }

      swiperResponsiveToggle()
      const throttledResize = utils.throttle(swiperResponsiveToggle, 100)
      window.addEventListener('resize', throttledResize)
    }
  }

  disconnectedCallback() {
    this.swiper?.destroy()
  }
}
document.addEventListener('DOMContentLoaded', function () {
  customElements.define('hh-swiper', HHSwiper)
});
